import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import MobileLayout from '../components/MobileLayout';
import ProtectedRoute from '../components/ProtectedRoute';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Flame, RefreshCw, Sparkles, ChevronRight } from 'lucide-react';
import { PointsBadge, PointsCelebration } from '../components/PointsDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const moods = [
  { emoji: '😊', label: 'Happy' },
  { emoji: '🥰', label: 'Loved' },
  { emoji: '😌', label: 'Calm' },
  { emoji: '🤩', label: 'Excited' },
  { emoji: '😈', label: 'Naughty' },
  { emoji: '😴', label: 'Tired' },
  { emoji: '😰', label: 'Anxious' },
  { emoji: '😢', label: 'Sad' },
  { emoji: '😤', label: 'Upset' },
];

// 30 rotating nice messages for streak celebrations
const streakMessages = [
  "You two are amazing together! 💕",
  "Your bond grows stronger every day!",
  "True love in action! Keep it up!",
  "You're building something beautiful!",
  "Communication is key, and you've got it!",
  "Your partner is lucky to have you!",
  "Together you're unstoppable!",
  "This is what real connection looks like!",
  "You're relationship goals! 🎯",
  "Love looks good on both of you!",
  "Keep nurturing this beautiful bond!",
  "Your dedication to each other shines!",
  "This streak shows real commitment!",
  "You're making love look easy!",
  "Partners in life, champions in love!",
  "Your hearts beat as one! 💗",
  "Consistency is the key to lasting love!",
  "You're writing your own love story!",
  "Every day together is a gift!",
  "Your love inspires us all!",
  "Two hearts, one beautiful journey!",
  "You've cracked the code to happiness!",
  "Your connection is extraordinary!",
  "Love like this is rare - cherish it!",
  "You're each other's biggest fans!",
  "This is what forever looks like!",
  "Your love grows with each passing day!",
  "Soulmates in sync! ✨",
  "You make being in love look fun!",
  "Here's to many more days together!"
];

const MoodSyncContent = ({ user }) => {
  // Onboarding state (show only once ever)
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(() => {
    return localStorage.getItem('moodSyncOnboardingSeen') === 'true';
  });
  
  const [selectedMood, setSelectedMood] = useState(null);
  const [sentence, setSentence] = useState('');
  const [streak, setStreak] = useState(0);
  const [streakAtRisk, setStreakAtRisk] = useState(false);
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showWaiting, setShowWaiting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [partnerName, setPartnerName] = useState('Partner');
  const [userPoints, setUserPoints] = useState(0);
  
  // Points celebration state
  const [showPointsCelebration, setShowPointsCelebration] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [pointsMessage, setPointsMessage] = useState('');
  
  const fetchData = useCallback(async () => {
    try {
      const [streakRes, todayRes, connectionRes, statusRes] = await Promise.all([
        axios.get(`${API}/moodsync/streak`, { withCredentials: true }),
        axios.get(`${API}/moodsync/today`, { withCredentials: true }),
        axios.get(`${API}/user/connection-info`, { withCredentials: true }),
        axios.get(`${API}/browny/status`, { withCredentials: true }).catch(() => ({ data: { user_points: 0 } }))
      ]);
      
      const newStreak = streakRes.data.streak;
      setStreak(newStreak);
      setStreakAtRisk(streakRes.data.streak_at_risk || false);
      setTodayData(todayRes.data);
      setUserPoints(statusRes.data?.user_points || 0);
      
      // Get partner's first name
      if (connectionRes.data?.partner?.name) {
        const firstName = connectionRes.data.partner.name.split(' ')[0];
        setPartnerName(firstName);
      }
      
      if (todayRes.data.user_entry) {
        const mood = moods.find(m => m.emoji === todayRes.data.user_entry.mood);
        setSelectedMood(mood);
        setSentence(todayRes.data.user_entry.sentence);
      }
      
      if (todayRes.data.both_completed) {
        setShowResults(true);
        setShowWaiting(false);
        
        // Show celebration ONLY ONCE per streak increment
        // Track the last streak we celebrated
        const lastCelebratedStreak = localStorage.getItem(`lastCelebratedStreak_${user?.user_id}`);
        const lastStreakNum = lastCelebratedStreak ? parseInt(lastCelebratedStreak) : 0;
        
        // Only show if this is a NEW streak count we haven't celebrated
        if (newStreak > lastStreakNum) {
          setShowCelebration(true);
          localStorage.setItem(`lastCelebratedStreak_${user?.user_id}`, String(newStreak));
          setTimeout(() => setShowCelebration(false), 3500);
        }
      } else if (todayRes.data.user_entry && !todayRes.data.partner_entry) {
        setShowWaiting(true);
        setShowResults(false);
      }
    } catch (error) {
      console.error('Error fetching mood data:', error);
    }
  }, [user?.user_id]);
  
  useEffect(() => {
    if (hasSeenOnboarding) {
      fetchData();
    }
  }, [hasSeenOnboarding, fetchData]);
  
  const handleSubmit = async () => {
    if (!selectedMood || !sentence.trim()) {
      toast.error('Please select a mood and write a sentence');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/moodsync`,
        { mood: selectedMood.emoji, sentence: sentence.trim() },
        { withCredentials: true, params: { mood: selectedMood.emoji, sentence: sentence.trim() } }
      );
      
      // Show points celebration if points were earned
      if (response.data.points_earned > 0) {
        setPointsEarned(response.data.points_earned);
        if (response.data.both_bonus) {
          setPointsMessage('You both logged your mood! Bonus points! 🎉');
        } else {
          setPointsMessage('Mood logged! Keep connecting! 💕');
        }
        setShowPointsCelebration(true);
      } else {
        toast.success('Mood updated!');
      }
      
      setShowWaiting(true);
      await fetchData();
    } catch (error) {
      toast.error('Failed to save mood');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdate = () => {
    setShowResults(false);
    setShowWaiting(false);
    setShowCelebration(false);
  };
  
  const completeOnboarding = () => {
    localStorage.setItem('moodSyncOnboardingSeen', 'true');
    setHasSeenOnboarding(true);
  };
  
  const getStreakMessage = () => {
    return streakMessages[streak % streakMessages.length];
  };
  
  // Celebration overlay - defined outside to avoid re-creation
  const renderCelebrationOverlay = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center animate-in fade-in duration-300">
      <div className="bg-white rounded-3xl p-8 mx-6 text-center animate-in zoom-in duration-500">
        <div className="relative">
          <Sparkles className="w-16 h-16 text-yellow-400 mx-auto animate-pulse" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-4xl">🎉</span>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mt-4">Streak Complete!</h2>
        <div className="flex items-center justify-center gap-2 my-4">
          <Flame className="w-8 h-8 text-orange-500" fill="currentColor" />
          <span className="text-5xl font-bold text-gradient">{streak}</span>
        </div>
        <p className="text-gray-600 text-lg">{getStreakMessage()}</p>
      </div>
    </div>
  );
  
  // Points celebration popup
  const renderPointsCelebration = () => (
    <PointsCelebration
      show={showPointsCelebration}
      points={pointsEarned}
      message={pointsMessage}
      onClose={() => setShowPointsCelebration(false)}
      currentTotal={userPoints}
    />
  );
  
  // ==================== ONBOARDING (First time only) ====================
  if (!hasSeenOnboarding) {
    return (
      <MobileLayout>
        <div className="p-6 flex flex-col items-center justify-center min-h-[calc(100vh-120px)]">
          <div className="text-center space-y-6 animate-in fade-in duration-500">
            <div className="text-7xl mb-4">💭</div>
            
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Once a Day</h1>
              <h1 className="text-2xl font-bold text-gradient">Keeps Conflict Away</h1>
            </div>
            
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-3 max-w-xs mx-auto">
              <p className="text-gray-600">
                Life is easier when you know <span className="font-semibold text-primary">How they Feel</span>
              </p>
              <p className="text-gray-500 text-sm">
                Make this your daily practice
              </p>
            </div>
            
            <Button
              onClick={completeOnboarding}
              className="w-full max-w-xs h-14 rounded-2xl gradient-brand shadow-pink text-white font-semibold mt-8"
            >
              Let&apos;s Start
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </MobileLayout>
    );
  }
  
  // Waiting for partner
  if (showWaiting && todayData?.user_entry && !todayData?.both_completed) {
    return (
      <MobileLayout>
        {renderPointsCelebration()}
        <div className="p-5 space-y-5" data-testid="moodsync-waiting">
          <div className="flex justify-between items-center pt-4">
            <h1 className="text-2xl font-bold text-gradient">CoupleIn</h1>
            <PointsBadge points={userPoints} />
          </div>
          
          {/* Streak */}
          <div className={`rounded-2xl p-4 text-center ${
            streak > 0 ? 'bg-orange-50' : 'bg-gray-50'
          }`}>
            <div className="flex items-center justify-center gap-2 mb-1">
              <Flame className={`w-6 h-6 ${streak > 0 ? 'text-orange-500' : 'text-gray-300'}`} fill="currentColor" />
              <span className={`text-3xl font-bold ${streak > 0 ? 'text-orange-500' : 'text-gray-400'}`}>{streak}</span>
            </div>
            <p className="text-sm text-gray-500">day streak</p>
          </div>
          
          {/* Your mood */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-3">Your mood today</p>
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-center">
                <span className="text-5xl">{todayData.user_entry.mood}</span>
                <span className="text-xs text-gray-500 mt-1 font-medium">
                  {moods.find(m => m.emoji === todayData.user_entry.mood)?.label || ''}
                </span>
              </div>
              <p className="text-gray-700 flex-1">{todayData.user_entry.sentence}</p>
            </div>
          </div>
          
          {/* Waiting indicator */}
          <div className="bg-primary/5 rounded-2xl p-6 text-center">
            <div className="flex justify-center gap-1.5 mb-3">
              <div className="w-2.5 h-2.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2.5 h-2.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2.5 h-2.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <p className="font-semibold text-gray-900">Waiting for {partnerName}</p>
            <p className="text-sm text-gray-500 mt-1">You&apos;ll see results once they submit</p>
          </div>
          
          <Button
            onClick={handleUpdate}
            variant="outline"
            className="w-full h-11 rounded-xl border-gray-200"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Update My Mood
          </Button>
        </div>
      </MobileLayout>
    );
  }
  
  // Both completed - show results
  if (showResults && todayData?.both_completed) {
    return (
      <MobileLayout>
        {showCelebration && renderCelebrationOverlay()}
        {renderPointsCelebration()}
        <div className="p-5 space-y-5" data-testid="moodsync-results">
          <div className="flex justify-between items-center pt-4">
            <h1 className="text-2xl font-bold text-gradient">CoupleIn</h1>
            <PointsBadge points={userPoints} />
          </div>
          
          {/* Streak celebration */}
          <div className="bg-gradient-brand rounded-2xl p-5 text-center shadow-pink">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Flame className="w-8 h-8 text-white" fill="white" />
              <span className="text-5xl font-bold text-white">{streak}</span>
            </div>
            <p className="text-white/90 font-medium">
              {streak === 1 ? 'Day Streak Started!' : 'Day Streak 🔥'}
            </p>
            <p className="text-white/70 text-sm mt-2">{getStreakMessage()}</p>
          </div>
          
          {/* Your mood */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-3">You</p>
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center">
                <span className="text-4xl">{todayData.user_entry.mood}</span>
                <span className="text-xs text-gray-500 mt-1">
                  {moods.find(m => m.emoji === todayData.user_entry.mood)?.label || ''}
                </span>
              </div>
              <p className="text-gray-700 pt-1">{todayData.user_entry.sentence}</p>
            </div>
          </div>
          
          {/* Partner mood */}
          <div className="bg-primary/5 rounded-2xl p-5">
            <p className="text-xs text-primary font-medium uppercase tracking-wider mb-3">{partnerName}</p>
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center">
                <span className="text-4xl">{todayData.partner_entry.mood}</span>
                <span className="text-xs text-gray-500 mt-1">
                  {moods.find(m => m.emoji === todayData.partner_entry.mood)?.label || ''}
                </span>
              </div>
              <p className="text-gray-700 pt-1">{todayData.partner_entry.sentence}</p>
            </div>
          </div>
          
          <Button
            onClick={handleUpdate}
            variant="outline"
            className="w-full h-11 rounded-xl border-gray-200"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Update My Mood
          </Button>
        </div>
      </MobileLayout>
    );
  }
  
  // Mood entry form
  return (
    <MobileLayout>
      {renderPointsCelebration()}
      <div className="p-5 space-y-5" data-testid="moodsync-entry">
        <div className="flex justify-between items-center pt-4">
          <h1 className="text-2xl font-bold text-gradient">CoupleIn</h1>
          <PointsBadge points={userPoints} />
        </div>
        
        {/* Streak indicator */}
        <div className={`rounded-2xl p-4 text-center ${
          streak > 0 ? 'bg-orange-50' : 'bg-gray-50'
        }`}>
          <div className="flex items-center justify-center gap-2">
            <Flame className={`w-5 h-5 ${streak > 0 ? 'text-orange-500' : 'text-gray-300'}`} fill="currentColor" />
            <span className={`text-2xl font-bold ${streak > 0 ? 'text-orange-500' : 'text-gray-400'}`}>{streak}</span>
            <span className="text-sm text-gray-500">day streak</span>
          </div>
          {streak === 0 && (
            <p className="text-xs text-gray-400 mt-2">Show each other how well you can work together!</p>
          )}
        </div>
        
        {/* Streak at risk warning */}
        {streakAtRisk && streak > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 animate-pulse">
            <div className="flex items-center gap-2 text-red-600">
              <Flame className="w-5 h-5" fill="currentColor" />
              <span className="font-semibold">Don&apos;t lose your {streak} day streak!</span>
            </div>
            <p className="text-sm text-red-500 mt-1">Submit your mood before midnight to keep it going!</p>
          </div>
        )}
        
        {/* Question */}
        <p className="text-center text-gray-600 font-medium">How are you feeling today?</p>
        
        {/* Mood grid */}
        <div className="grid grid-cols-3 gap-3">
          {moods.map((mood) => (
            <button
              key={mood.label}
              data-testid={`mood-${mood.label.toLowerCase()}`}
              onClick={() => setSelectedMood(mood)}
              className={`aspect-square rounded-2xl flex flex-col items-center justify-center gap-1.5 tap-mood ${
                selectedMood?.label === mood.label
                  ? 'bg-primary/10 ring-2 ring-primary shadow-pink'
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <span className="text-3xl">{mood.emoji}</span>
              <span className={`text-xs font-medium ${
                selectedMood?.label === mood.label ? 'text-primary' : 'text-gray-500'
              }`}>{mood.label}</span>
            </button>
          ))}
        </div>
        
        {/* Sentence input */}
        {selectedMood && (
          <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <label className="text-sm font-medium text-gray-700">Tell your partner why:</label>
            <Textarea
              value={sentence}
              onChange={(e) => setSentence(e.target.value)}
              placeholder="I feel this way because..."
              className="min-h-[100px] rounded-xl border-gray-200 resize-none"
              maxLength={200}
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">{sentence.length}/200</span>
            </div>
            
            <Button
              onClick={handleSubmit}
              disabled={loading || !sentence.trim()}
              className="w-full h-12 rounded-xl gradient-brand shadow-pink font-semibold text-white"
            >
              {loading ? 'Saving...' : todayData?.user_entry ? 'Update' : 'Submit'}
            </Button>
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

const MoodSync = () => {
  return (
    <ProtectedRoute>
      <MoodSyncContent />
    </ProtectedRoute>
  );
};

export default MoodSync;
